import { useCallback, useMemo } from 'react'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../../../../utils/globals/dictionary'
import ITHeaderText, { ITHeaderTextCustomer, ITHeaderTextSupplier } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormElement, FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { parseAddressToOneLine } from '../../../../../../../../../../../utils/globals/address'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../../../utils/reducers/getters'
import { WorkingCenterPayload } from '../../../../../../../../../../../utils/reducers/company/workingCenters'
import { CustomSelectWithSearchBar } from '../../../../../../../../../../../forms/CustomSelect'
import { SetCompleteDataTy } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { BuyTagsPayload } from '../../../../../../../../../../../utils/reducers/buys/buyTags'
import { BuyInvoiceModelType } from '@teinor/erp/types/company/order/buy/buyInvoice'
import { notHaveCountersAndWarehousesWorkingCenter } from '../../../../../../../../../../../utils/globals/buy/buy'
import { EmployeesPayload } from '../../../../../../../../../../../utils/reducers/company/employees'
import { sortEmployeesByUser } from '../../../../../../../../../../../utils/globals/employees'
import { ITHeaderOrderTags } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderOrderTags'
import { InputWithLabelMargin } from '../../../../../../../../../../../forms/Input'

type BuyInvoiceCreateStepHeaderMainProps = InputTableLeftTabsProps & BuyInvoiceCreateStepHeaderMainLogicProps & {
    buyTags: BuyTagsPayload
    employees: EmployeesPayload
}

const BuyInvoiceCreateStepHeaderMain: React.FC<BuyInvoiceCreateStepHeaderMainProps> = ({ employees, workingCenters, getCompleteData, handleChange, headerData, buyTags,  ...logic }) => {
    const { t } = useTwinTranslation()
    const { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress } = useBuyInvoiceCreateStepHeaderMainLogic({ headerData, workingCenters, handleChange, ...logic})
    const extraCustomer: FormElement<keyof BuyInvoiceModelType>[] = []
    if(headerData?.CustomerId){
        extraCustomer.push(
            {
                name: 'CustomerId',
                component: 'Custom',
                render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
            },
        )
    }
    const previousDeliveryNote = headerData?.BuyDeliveryNoteId
    let flexItems: FormElement<keyof BuyInvoiceModelType>[] = [
        {
            name: 'WorkingCenterId',
            component: 'CustomSelectBlockedItemsSearchbar',
            label: t('buyBy', 'Comprado por'),
            items: workingCenters || {},
            blockItemsFnc: notHaveCountersAndWarehousesWorkingCenter,
            onChange: getCompleteData,
            className: returnClassOfSpanTemplate(5)
        },
        {
            name: 'WarehouseId',
            component: 'CustomSelectWithSearchBar',
            label: t('deliveryTo', 'Entregar a'),
            items: dictWarehouses,
            sortItems: true,
            className: returnClassOfSpanTemplate(5)
        },
    ]
    if (previousDeliveryNote) {
        flexItems = [
            {
                name: 'WorkingCenterId',
                component: 'Custom',
                render: () => <ITHeaderText label={t('buyBy', 'Comprado por')} value={headerData?.WorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
            },
            {
                name: 'WarehouseId',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('deliveredTo', 'Entregado a')} value={dictWarehouses?.[value]?.name} className={returnClassOfSpanTemplate(2)} />
            },
        ]
    }
    const sections: FormRender<BuyInvoiceModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'SupplierId',
                    component: 'Custom',
                    render: () => <ITHeaderTextSupplier label={t('supplier', 'Proveedor')} headerData={headerData} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                ...extraCustomer,
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('representative', 'Representante'),
                    items: employees || {},
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(5),
                    sortItems: true,
                    fieldName: 'fullname_short',
                    sortFunction: (emp) => sortEmployeesByUser(emp, false)
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value } ) => <ITHeaderText label={t('draftN', 'Nº de borrador')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'processed_date',
                    component: 'InputCalendarStateFull',
                    label: t('dateProcessed', 'Fecha de recepción'),
                    onlyValids: true,
                    required: true,
                    className: (headerData?.processed_date === null || headerData?.processed_date === '' ? ' input_error ' : '') + returnClassOfSpanTemplate(5)
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'InputWithLabelMargin',
                    label: t('supplierInvoice', 'Factura del proveedor'),
                    className: returnClassOfSpanTemplate(5),
                    required: true,
                },
                {
                    name: 'supplier_date',
                    component: 'InputCalendarStateFull',
                    label: t('dateSupplier', 'Fecha del proveedor'),
                    onlyValids: true,
                    required: true,
                    className: returnClassOfSpanTemplate(5),
                },
                {
                    name: 'supplier_number_order',
                    component: 'InputWithLabelMargin',
                    label: t('supplierNumberOrder', 'Num. pedido proveedor'),
                    className: returnClassOfSpanTemplate(5),
                },
                ...flexItems,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'SupplierAddress',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('addressSupplier', 'Dirección proveedor')} value={parseAddressToOneLine(headerData.Supplier)} className={returnClassOfSpanTemplate(5)} />
                    },
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: { workingCenterAddress }
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectWithSearchBar label={t('currency', 'Moneda')} items={extraProps.activeCurrencies} value={extraProps.CurrencyId} onChange={extraProps.changeCurrencyId} className={returnClassOfSpanTemplate(2)} isStateLess={true} />
                    },
                    extraProps: {
                        activeCurrencies,
                        CurrencyId: headerData.CurrencyId,
                        changeCurrencyId
                    }
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={buyTags} headerData={headerData} onChange={handleChange} {...logic} className={returnClassOfSpanTemplate(5)} />)
                    },
                    extraProps: { selectedTags: headerData?.tags }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}

interface BuyInvoiceCreateStepHeaderMainLogicProps {
    headerData: TwinDictionary
    workingCenters: WorkingCenterPayload
    setCompleteData: SetCompleteDataTy
    handleChange: InputTableLeftColumnChange
}

export const useBuyInvoiceCreateStepHeaderMainLogic = ({ workingCenters, headerData, setCompleteData, handleChange }: BuyInvoiceCreateStepHeaderMainLogicProps) => {
    const WorkingCenterId = headerData.WorkingCenterId
    const CurrencyId = headerData.CurrencyId
    const activeCurrencies = getActiveCurrenciesDictionary()

    const dictWarehouses = useMemo(() => dictionaryComplexFromJsonArr(workingCenters?.[parseInt(WorkingCenterId || '1')]?.Warehouses || []), [workingCenters, WorkingCenterId])
    const WorkingCenter = headerData.BillingWorkingCenter
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(WorkingCenter), [WorkingCenter])
    const changeCurrencyId = useCallback((value: string) => {
        if (value !== CurrencyId){
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.header.CurrencyId = value
                return { ...copyOld }
            })
            handleChange(headerData?.id, 'CurrencyId', value)
        }
    }, [CurrencyId, handleChange, headerData?.id, setCompleteData])


    return { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress }
}


export default BuyInvoiceCreateStepHeaderMain